import DOMPurify from 'dompurify'
import { marked } from 'marked'
import {
  getHeadingList,
  gfmHeadingId,
  type HeadingData
} from 'marked-gfm-heading-id'
import { stripHtml } from 'string-strip-html'
import type { Ref } from 'vue'

marked.use(gfmHeadingId())

const markedOptions = {
  gfm: true,
  breaks: true
}

/**
 * Parse description from markdown to HTML
 */
export const descriptionFromMarkdown = (ref: Ref, attr = 'description') => {
  if (ref.value[attr]) {
    return fromMarkdown(ref.value[attr]).html
  }
}

/**
 * Parse markdown to HTML with headings
 */
export const fromMarkdown = (value: string | null, inline: boolean = false) => {
  if (!value) return { html: '', headings: [] }
  let headings: HeadingData[] = []
  marked.use(gfmHeadingId(), {
    hooks: {
      postprocess(html) {
        headings = getHeadingList()
        return html
      }
    }
  })
  const fn = inline ? marked.parseInline : marked.parse
  const parsed = fn(value, markedOptions)
  // type cast to string because we don't use async mode of marked
  return { html: DOMPurify.sanitize(parsed as string), headings }
}

/**
 * Strip HTML tags from markdown
 */
export const stripFromMarkdown = (value: string, maxSize?: number) => {
  const html = marked.parse(value, markedOptions)
  // type cast to string because we don't use async mode of marked
  const text = stripHtml(html as string).result
  if (maxSize === undefined || text.length <= maxSize) return text
  return text.slice(0, maxSize).replace(/\S+$/, '').trimEnd() + '…'
}

/**
 * Format date
 */
export const formatDate = (dateString: string, short = false) => {
  const date = new Date(dateString)
  const params: Intl.DateTimeFormatOptions = short
    ? {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }
    : {
        dateStyle: 'full',
        timeStyle: 'short'
      }
  return new Intl.DateTimeFormat('default', params).format(date)
}

/**
 * Basic slugify function. Deletes special characters like numbers or diacritics instead of converting them.
 */
export const basicSlugify = (value: string) => {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '-')
}
